import AppConfig from '@/app-config'
import React, { useEffect } from 'react'
import useStore from '@/store'
import classNames from 'classnames'
import type { Props } from './types'
import Locations from '@/components/locations'
import styles from './style.module.scss'
import LinkPoint from '@/components/link-point'
import ResidentsModalContent from '@/components/modal/residents-modal-content'
import TenantsModalContent from '@/components/modal/tenants-modal-content'
import { CampusType, ModalSide } from '@/store/types'
import useMeasure from '@/hooks/use-measure'

const Basel: React.FC<Props> = ({ customRef }) => {
  const onPointerMove = useMeasure('basel', false, customRef)
  const setActiveMode = useStore((state) => state.setActiveMode)
  const setModal = useStore((state) => state.setModal)
  const isIdle = useStore((state) => state.isIdle)
  const locationsData = useStore((state) => state.locationsData)
  const residentsData = useStore((state) => state.residentsData)
  const campusesData = useStore((state) => state.campusesData)
  const setAvailableCategories = useStore(
    (state) => state.setAvailableCategories
  )
  const classes = classNames(styles['basel-view'], {
    [styles['is-idle']]: isIdle,
  })

  const baselLocationsData = locationsData?.filter(
    (l) => l.attributes.Position.Map === 'Basel'
  )

  useEffect(() => {
    setModal({ content: undefined }, 'top')
    setModal({ content: undefined }, 'bottom')
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (locationsData)
      setAvailableCategories([
        ...new Set(
          baselLocationsData?.map(
            (l) => l.attributes.Category.data.attributes.Slug
          )
        ),
      ])
    // eslint-disable-next-line
  }, [locationsData])

  function onLinkPointClick(type: CampusType, side: ModalSide, title: string,
    overviewLabel: string, overviewImageURL: string) {
    if (type === 'Switzerland')
      setActiveMode('switzerland')
    else {
      const all = residentsData?.filter(
        (r) => r.attributes.Campus.data.attributes.Type === type
      ) || []
      const tenants = all.filter((r) => r.attributes.Level === 'Overview')
      const residents = all.filter((r) => r.attributes.Level !== 'Overview')

      setModal(
        {
          content: tenants.length > 0 ? (
            <TenantsModalContent
              type={type}
              side={side}
              tenants={tenants}
              residents={residents}
              overviewLabel={overviewLabel}
              overviewImageURL={`${AppConfig.apiUrl}${overviewImageURL}`}
            />
          ) : (
            <ResidentsModalContent
              type={type}
              side={side}
              building={residents?.find(
                (r) => r.attributes.Building.data)?.attributes.Building.data}
              residents={residents}
            />
          ),
          type: 'link-point',
          closeAction: () =>
            setModal({ content: undefined, type: 'link-point' }, side),
        },
        side
      )
    }
  }

  return (
    <div ref={customRef} className={classes} onPointerMove={onPointerMove}>
      <figure className={styles['bg-river']}>
        <video width="100%" height="100%" autoPlay loop muted playsInline>
          <source
            src={require('@/assets/videos/river.webm')}
            type="video/webm"
          />
        </video>
      </figure>
      <figure
        className={styles['bg-map']}
        style={{
          backgroundImage: `url(${require('@/assets/images/bg-basel_1200.png')})`,
        }}
      />
      <Locations locations={baselLocationsData} />
      <div className={styles['link-points']}>
        {campusesData?.map((c) => (
          <LinkPoint
            key={c.id}
            title={c.attributes.Title}
            label={c.attributes.Label}
            type={c.attributes.Type}
            position={c.attributes.Position}
            labelPlacement={c.attributes.LabelPlacement}
            residents={
              c.attributes.Type === 'Switzerland'
                ? locationsData?.filter(
                    (l) => l.attributes.Position.Map === 'Switzerland'
                  )
                : residentsData?.filter(
                    (r) =>
                      r.attributes.Campus.data.attributes.Type ===
                        c.attributes.Type && r.attributes.Level !== 'Overview'
                  )
            }
            clickHandler={() =>
              onLinkPointClick(
                c.attributes.Type,
                c.attributes.Position.Mirrored ? 'top' : 'bottom',
                c.attributes.Title,
                c.attributes.OverviewLabel,
                c.attributes.OverviewImage.data?.attributes.url || ''
              )
            }
          />
        ))}
      </div>
    </div>
  )
}

export default React.memo(Basel)
